import { useEffect, useRef } from "react";
import type { UserDto } from "../api";
import { LOCAL_STORAGE_KEYS } from "../utils/localStorageKeys";
import { useCartStore } from "./stores/useCartStore";

/**
 * Get the user-specific storage key
 */
function getCartStorageKey(userId: string | null): string {
    return userId ? `cart-storage-${userId}` : "cart-storage-guest";
}

/**
 * Hook to sync user ID to localStorage and handle cart migration when user changes
 */
export function useUserCartSync(user: UserDto | undefined) {
    const previousUserIdRef = useRef<string | null>(
        typeof window !== "undefined"
            ? localStorage.getItem(LOCAL_STORAGE_KEYS.CURRENT_USER_ID)
            : null
    );
    const clearCart = useCartStore((state) => state.clearCart);
    const setState = useCartStore.setState;
    const rehydrate = useCartStore.persist.rehydrate;
    const hasHydrated = useCartStore.persist.hasHydrated();

    useEffect(() => {
        const currentUserId = user?.id ?? null;
        const previousUserId = previousUserIdRef.current;

        // Store current user ID in localStorage
        if (currentUserId) {
            localStorage.setItem(LOCAL_STORAGE_KEYS.CURRENT_USER_ID, currentUserId);
        } else {
            localStorage.removeItem(LOCAL_STORAGE_KEYS.CURRENT_USER_ID);
        }

        // Helper function to load cart from storage
        const loadCartFromStorage = (userId: string | null) => {
            const storageKey = getCartStorageKey(userId);
            const cartData = localStorage.getItem(storageKey);

            if (cartData) {
                try {
                    const parsed = JSON.parse(cartData);
                    if (parsed?.state?.items && Array.isArray(parsed.state.items)) {
                        setState({ items: parsed.state.items });
                        return true;
                    }
                } catch (error) {
                    console.error("Error loading cart:", error);
                }
            }
            return false;
        };

        // Skip if user hasn't changed and we're not in initial load
        const userChanged = previousUserId !== currentUserId;
        const isInitialLoad = previousUserId === null && !hasHydrated;

        // If user changed (and we had a previous user), migrate cart
        if (userChanged && previousUserId !== null) {
            // Clear current cart state
            clearCart();

            // Load cart from new user's storage (if logging in, not logging out)
            if (currentUserId) {
                loadCartFromStorage(currentUserId);
            }
        } else if (isInitialLoad) {
            // First time - need to rehydrate
            // Rehydrate will use the current user ID from localStorage (if set)
            rehydrate();
        } else if (currentUserId && userChanged && previousUserId === null) {
            // User just logged in (previousUserId was null, now we have a user)
            // This happens after logout -> login
            // Load user-specific cart
            const loaded = loadCartFromStorage(currentUserId);
            if (!loaded) {
                // No cart data found, ensure cart is empty
                setState({ items: [] });
            }
        } else if (currentUserId && previousUserId === currentUserId) {
            // Same user - ensure cart is loaded (in case of page refresh)
            // Only check if we haven't loaded it yet
            const currentItems = useCartStore.getState().items;
            if (currentItems.length === 0) {
                loadCartFromStorage(currentUserId);
            }
        }

        // Update the ref for next comparison
        previousUserIdRef.current = currentUserId;
    }, [ user?.id, clearCart, setState, rehydrate, hasHydrated ]);
}

